import { useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { TriangleAlertIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  InnoIntelUser,
  InnointelCompanyListItem,
} from "components/innointel/types";
import { useTrayStatus } from "components/lib/Notifications";

import { CompanyLogo } from "./CompanyLogo";
import { ContactStatusPill } from "./ContactStatusPill";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (email: string) => EMAIL.test(email.trim());

const Recipients = styled.div`
  .company {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-weight: 600;
  }

  .warning {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 12px;
    color: #b45309;
  }
`;

const Section = styled.div`
  & + & {
    margin-top: 24px;
  }

  h4 {
    margin: 0 0 4px;
  }

  .description {
    margin: 0 0 12px;
    font-size: 13px;
    color: #5b6470;
  }
`;

export type ContactTarget =
  | { listId: string; searchId?: undefined; name: string }
  | { searchId: string; listId?: undefined; name: string };

type ContactCompaniesModalProps = {
  companies: InnointelCompanyListItem[];
  /** The list or search the companies were picked from; without one, e.g. from a company's messages, there is
   * nothing to export */
  target?: ContactTarget;
  onClose: () => void;
};

/** Emails, or exports, the companies selected in a list or search. Each email can be corrected before sending. */
export const ContactCompaniesModal = ({
  companies,
  target,
  onClose,
}: ContactCompaniesModalProps) => {
  const user = useCurrentUser() as InnoIntelUser | null;
  const { t } = useTranslation();
  const [, setTrayStatus] = useTrayStatus();
  const [emails, setEmails] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      companies.map((company) => [
        company._id,
        (company as { email?: string }).email ?? "",
      ]),
    ),
  );
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(user?.emails[0]?.address ?? "");
  const [isSending, setIsSending] = useState(false);

  const recipients = companies
    .filter((company) => isValidEmail(emails[company._id] ?? ""))
    .map((company) => ({
      companyId: company._id,
      email: emails[company._id]!.trim(),
    }));
  const skipped = companies.length - recipients.length;
  const params = target?.listId
    ? { listId: target.listId }
    : target?.searchId
      ? { searchId: target.searchId }
      : {};

  const refresh = () => {
    companies.forEach((company) =>
      api.invalidateQueries(api.innointel.getCompany, [company._id]),
    );
    if (target?.listId) {
      api.invalidateQueries(api.innointel.getList, [target.listId]);
      api.invalidateQueries(api.innointel.getListCompanies, [target.listId]);
    } else if (target?.searchId) {
      api.invalidateQueries(api.innointel.getSearchCompanies, [
        target.searchId,
      ]);
    }
  };

  const sendEmails = () => {
    setIsSending(true);
    api.innointel.emailCompanies(
      params,
      { emailSubject: subject, emailText: text, emails: recipients, replyTo },
      () => {
        toast.success(
          `Emailed ${util.pluralise(recipients.length, "company", "companies", true)}`,
        );
        setIsSending(false);
        refresh();
        onClose();
      },
      (error) => {
        toast.error(error.message);
        setIsSending(false);
      },
    );
  };

  const downloadCsv = () => {
    if (!user?._id || !target) {
      return;
    }
    api.users.createDownload(
      user._id,
      {
        downloadType: "innointelCompaniesCsvExport",
        displayName: `${target.name} companies.csv`,
        ...(target.listId
          ? { list: target.listId }
          : { search: target.searchId }),
        emails: companies.map((company) => ({
          companyId: company._id,
          email: emails[company._id] ?? "",
        })),
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            total: (prev?.total ?? 0) + 1,
            downloads: [download, ...(prev?.downloads || [])],
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  };

  return (
    <Modal open onClose={onClose} size="large">
      <ModalHeader>
        Contact {util.pluralise(companies.length, "company", "companies", true)}
      </ModalHeader>
      <ModalContent>
        <Section>
          <H4>Recipients</H4>
          <p className="description">
            Check each email before sending. Companies without a valid email
            won&apos;t be contacted.
          </p>
          <Recipients>
            <Table compact size="small" maxHeight={320}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Company</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => {
                  const email = emails[company._id] ?? "";
                  return (
                    <TableRow key={company._id}>
                      <TableCell>
                        <span className="company">
                          <CompanyLogo
                            url={company.images?.logo?.url}
                            size={28}
                          />
                          {company.name}
                        </span>
                      </TableCell>
                      <TableCell collapsing>
                        <ContactStatusPill contact={company.contact} />
                      </TableCell>
                      <TableCell>
                        <Input
                          fluid
                          size="small"
                          value={email}
                          placeholder="Add an email..."
                          onChange={(_e, { value }) =>
                            setEmails((prev) => ({
                              ...prev,
                              [company._id]: value ?? "",
                            }))
                          }
                        />
                        {!isValidEmail(email) ? (
                          <span className="warning">
                            <Icon icon={TriangleAlertIcon} />
                            {email.trim()
                              ? "Not a valid email, won't be contacted"
                              : "No email, won't be contacted"}
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Recipients>
          {skipped ? (
            <Message color="orange" style={{ marginTop: 12 }}>
              {util.pluralise(skipped, "company has", "companies have", true)}{" "}
              no valid email and won&apos;t be contacted.
            </Message>
          ) : null}
        </Section>
        <Section>
          <H4>Email</H4>
          <FormField>
            <FormLabel>Subject</FormLabel>
            <Input
              fluid
              placeholder="Enter your email subject..."
              value={subject}
              onChange={(_e, { value }) => setSubject(value ?? "")}
            />
          </FormField>
          <FormField>
            <FormLabel>Message</FormLabel>
            <TextArea
              value={text}
              placeholder="Enter your email text..."
              onChange={(_e, { value }) =>
                setText(typeof value === "string" ? value : String(value ?? ""))
              }
            />
          </FormField>
          <FormField>
            <FormLabel>Reply-to address</FormLabel>
            <Input
              fluid
              placeholder={t("placeholder.email")}
              value={replyTo}
              onChange={(_e, { value }) => setReplyTo(value ?? "")}
            />
          </FormField>
        </Section>
      </ModalContent>
      <ModalActions>
        {target ? (
          <Button variant="outline" onClick={downloadCsv}>
            Download CSV
          </Button>
        ) : null}
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          loading={isSending}
          disabled={!recipients.length || !subject || !text}
          tooltip={
            !recipients.length
              ? "None of the selected companies has a valid email"
              : !subject || !text
                ? "Add a subject and a message first"
                : null
          }
          onClick={sendEmails}
        >
          Email{" "}
          {util.pluralise(recipients.length, "company", "companies", true)}
        </Button>
      </ModalActions>
    </Modal>
  );
};
